import { Trans, useLingui } from '@lingui/react/macro';
import type { RotaProposal } from '@repo/contracts';
import { ArrowLeft, CheckCircle2, Layers, TriangleAlert, X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCommitError, type FormatCommitErrorMessages } from '@/domain/rotagen/commit-error';
import {
  buildProposalDays,
  buildProposalStacks,
  diagnosticsForShift,
  totalShortCount,
  workerConflicts,
} from '@/domain/rotagen/proposal-day-data';
import { MAX_PREVIEW_PILLS_PER_DAY } from '@/domain/templates/preview-columns';
import { formatDayFull, formatDayShort } from '@/domain/templates/preview-utils';
import { cn } from '@/lib/utils';

import { ProposalShiftCell } from './proposal-shift-cell';
import { ProposalShiftStack } from './proposal-shift-stack';
import { WorkerConflicts } from './worker-conflicts';

interface ProposalViewProps {
  /** The generated proposal to review. */
  proposal: RotaProposal;
  /** Commit the proposal (with the replace-existing-drafts consent). */
  onCommit: (options: { replaceExistingRota: boolean }) => void | Promise<void>;
  /** Discard the proposal and close. */
  onDiscard: () => void;
  /** Go back to the generate setup to adjust inputs (site/week) and regenerate. */
  onAdjust: () => void;
  /** The last commit error, shown inline above the consent. */
  commitError?: unknown;
  /** Disables the actions while a commit is in flight. */
  isCommitting?: boolean;
}

/**
 * Read-only review of a generated rota (the v2 Build workspace's proposal
 * modal): a header, a summary strip (filled / unfilled / % filled), a
 * scrollable body with an "unfilled shifts" callout and a per-person pill grid
 * (one pill per head; unfilled shifts render as "short" with a per-shift "why"
 * popover), and a commit footer. No assign/reassign — the manager commits, or
 * regenerates after editing the template. Shares the prop contract of the
 * legacy `RotaProposalPreview` so the landing page swaps between them by flag.
 */
export function ProposalView({
  proposal,
  onCommit,
  onDiscard,
  onAdjust,
  commitError,
  isCommitting = false,
}: ProposalViewProps) {
  const { t } = useLingui();
  const [replaceExistingRota, setReplaceExistingRota] = useState(false);
  // Stacked (decks of same type+time) by default, mirroring the live preview,
  // so a manager can see the shape of the generated rota at a glance.
  const [stacked, setStacked] = useState(true);

  const days = buildProposalDays(proposal);
  const totalShifts = days.reduce((total, day) => total + day.cells.length, 0);
  const short = totalShortCount(days);
  const filled = totalShifts - short;
  const pctFilled = Math.round((filled / Math.max(1, totalShifts)) * 100);
  const hasShifts = totalShifts > 0;
  const subtitle = [proposal.template.name, proposal.site.name, weekLabel(proposal)]
    .filter(Boolean)
    .join(' · ');

  const errorMessages: FormatCommitErrorMessages = {
    consentHint: t`Tick "Replace any existing draft shifts in this period." below and retry.`,
    fallbackMessage: t`Something went wrong while committing.`,
  };
  const errorDisplay = commitError
    ? formatCommitError(commitError, replaceExistingRota, errorMessages)
    : null;

  return (
    <div className="flex max-h-[85vh] flex-col">
      <header className="flex items-start justify-between gap-4 border-b px-6 py-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">
            <Trans>Proposed rota</Trans>
          </h2>
          <p className="truncate text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
            {subtitle}
          </p>
        </div>
        <Button variant="ghost" size="icon" aria-label={t`Close`} onClick={onDiscard}>
          <X className="size-4" />
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b bg-muted/30 px-6 py-3">
        <Badge variant="success" className="gap-1">
          <CheckCircle2 className="size-3" />
          {filled} <Trans>filled</Trans>
        </Badge>
        <Badge variant="warning" className="gap-1">
          <TriangleAlert className="size-3" />
          {short} <Trans>unfilled</Trans>
        </Badge>
        <span className="ml-auto text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
          <Trans>{pctFilled}% of shifts filled</Trans>
        </span>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
        <WorkerConflicts conflicts={workerConflicts(proposal)} />

        {hasShifts ? (
          <>
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="icon"
                aria-pressed={stacked}
                aria-label={t`Stack shifts of the same type`}
                onClick={() => setStacked((value) => !value)}
                // A solid `secondary` (dark) background with a `text-primary` icon
                // on top is a ~1.2:1 contrast ratio — well under the 3:1 WCAG
                // minimum for UI components. `--button-toggle-active-background`
                // is a light tint that keeps the primary-coloured icon readable
                // when toggled on.
                className={cn(stacked && 'bg-[var(--button-toggle-active-background)]')}
              >
                <Layers className={cn('size-4', stacked && 'text-primary')} />
              </Button>
            </div>
            {/* px/pt give the absolutely-positioned ×N deck badges room so they
                don't poke past the edge and trigger a horizontal scrollbar. */}
            <div className="flex gap-2 overflow-x-auto px-2 pt-2">
              {days.map((day) => (
                <div
                  key={day.date}
                  data-testid={`proposal-day-${day.date}`}
                  className="flex min-w-[112px] flex-1 flex-col gap-1"
                >
                  <div className="pb-1 text-center text-lg font-semibold leading-tight text-[var(--heading-foreground)]">
                    <span>{formatDayShort(day.date)}</span> <span>{formatDayFull(day.date)}</span>
                  </div>
                  {stacked ? (
                    buildProposalStacks(day.cells).map((stack) => (
                      <ProposalShiftStack
                        key={stack.key}
                        stack={stack}
                        diagnostics={proposal.diagnostics}
                      />
                    ))
                  ) : (
                    <>
                      {day.cells.slice(0, MAX_PREVIEW_PILLS_PER_DAY).map((cell) => (
                        <ProposalShiftCell
                          key={cell.shift.id}
                          cell={cell}
                          diagnostics={diagnosticsForShift(proposal.diagnostics, cell.shift.id)}
                        />
                      ))}
                      {day.cells.length > MAX_PREVIEW_PILLS_PER_DAY && (
                        <div className="rounded-[3px] border border-dashed py-1 text-center text-xs font-medium text-muted-foreground">
                          {t`+${day.cells.length - MAX_PREVIEW_PILLS_PER_DAY} more`}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="rounded-md border border-dashed p-8 text-center text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
            <Trans>No shifts were generated for this date range.</Trans>
          </p>
        )}
      </div>

      <footer className="space-y-3 border-t px-6 py-4">
        {errorDisplay && (
          <div
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm whitespace-pre-line text-destructive"
          >
            {errorDisplay}
          </div>
        )}

        {hasShifts && (
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={replaceExistingRota}
              onCheckedChange={(checked) => setReplaceExistingRota(checked === true)}
              aria-label={t`Replace existing draft shifts`}
            />
            <span>
              <span className="font-medium">
                <Trans>Replace any existing draft shifts in this period.</Trans>
              </span>
              <span className="ml-1 text-muted-foreground">
                <Trans>Off by default — only enable if you intend to overwrite drafts.</Trans>
              </span>
            </span>
          </label>
        )}

        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={onAdjust} disabled={isCommitting}>
            <ArrowLeft className="size-4" />
            <Trans>Adjust inputs</Trans>
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onDiscard} disabled={isCommitting}>
              <Trans>Cancel</Trans>
            </Button>
            {hasShifts && (
              <Button onClick={() => onCommit({ replaceExistingRota })} disabled={isCommitting}>
                {isCommitting ? <Trans>Committing…</Trans> : <Trans>Confirm & add to rota</Trans>}
              </Button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

/** A "22 Jun – 28 Jun" label spanning the proposal's days. */
function weekLabel(proposal: RotaProposal): string {
  const { days } = proposal;
  if (days.length === 0) return '';
  return `${formatDayFull(days[0].date)} – ${formatDayFull(days[days.length - 1].date)}`;
}
